// File: src/bridge/omnicine/export/installExportAudioResolver.ts
//
// installExportAudioResolver — runtime WAV resolver hook (no hard coupling)
// -----------------------------------------------------------------------
// Installs:
// - window.__oc_export.resolvePodcastMedia(args)  (preferred modular hook)
// - window.__oc_export.resolvePodcastAudio(args)  (preferred modular hook)
// - window.__oc_export.resolveAudio(clip, track, exp) (legacy adapter for RenderPodcastWavOptions)
// - window.__oc_export.renderPodcastWav(exp, opts?) -> Promise<Blob | { blob, filename? }>
//
// Marks:
// - window.__oc_export._audioResolverInstalled = true
//
// Uses:
// - normalizeClipMediaRef(clip) -> MediaRefV1
// - resolveMedia(ref) -> ResolvedMediaV1 { url, ... }
// - fetch + decodeAudioData -> AudioBuffer
// - resample/coerce to target sampleRate + channels via OfflineAudioContext

import type {
  RenderPodcastWavOptions,
  PodcastClipV1,
  PodcastTrackV1,
  PodcastExportV1,
} from "@/services/omnicine/export/podcastExportV1";
import type {
  OmniCineExportGlobal,
  PodcastResolveArgs,
} from "@/bridge/omnicine/export/exportGlobal";

import { renderPodcastExportToWavBlob } from "@/services/omnicine/export/podcastExportV1";
import { normalizeClipMediaRef } from "@/services/media/contracts/normalizeClipMediaRef";
import { resolveMedia } from "@/services/media/resolve/resolveMedia";

// ✅ Patch 3A (imports where you mix audio)
import { getPodcastRenderHintsFromResolved } from "@/services/media/registry/podcastMetaV1";
import type { ResolvedMediaV1 } from "@/services/media/resolve/resolveMedia";

type ResolveAudioFn = RenderPodcastWavOptions["resolveAudio"];

type InstallOptions = {
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
};

type ResolveArgs = PodcastResolveArgs;

function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  if (!(ms > 0)) return p;
  let t: any;
  const timeout = new Promise<T>((_, rej) => {
    t = setTimeout(() => rej(new Error(`Timeout (${ms}ms): ${label}`)), ms);
  });
  return Promise.race([p, timeout]).finally(() => clearTimeout(t));
}

async function fetchArrayBuffer(fetchImpl: typeof fetch, url: string, timeoutMs: number): Promise<ArrayBuffer> {
  const res = await withTimeout(fetchImpl(url), timeoutMs, `fetch ${url}`);
  if (!res.ok) throw new Error(`Fetch failed ${res.status} ${res.statusText} for ${url}`);
  return await withTimeout(res.arrayBuffer(), timeoutMs, `arrayBuffer ${url}`);
}

async function decodeAudio(arrayBuffer: ArrayBuffer): Promise<AudioBuffer> {
  // Decode using a short-lived AudioContext for broad compatibility.
  const Ctx = (window.AudioContext || (window as any).webkitAudioContext) as any;
  if (!Ctx) throw new Error("WebAudio AudioContext is not available in this environment");

  const ctx: AudioContext = new Ctx();
  try {
    // decodeAudioData sometimes mutates the buffer; slice to be safe.
    const buf = arrayBuffer.slice(0);
    const audio = await ctx.decodeAudioData(buf as any);
    return audio;
  } finally {
    try {
      await (ctx as any).close?.();
    } catch {
      // ignore
    }
  }
}

function buildChannelCoercer(
  ctx: OfflineAudioContext,
  src: AudioNode,
  srcChannels: number,
  dstChannels: number,
): AudioNode {
  if (dstChannels === srcChannels) return src;

  // Mono -> Stereo (duplicate)
  if (dstChannels === 2 && srcChannels === 1) {
    const splitter = ctx.createChannelSplitter(1);
    const merger = ctx.createChannelMerger(2);
    src.connect(splitter);
    splitter.connect(merger, 0, 0);
    splitter.connect(merger, 0, 1);
    return merger;
  }

  // Stereo+ -> Mono (average all channels)
  if (dstChannels === 1 && srcChannels >= 1) {
    const splitter = ctx.createChannelSplitter(srcChannels);
    const merger = ctx.createChannelMerger(1);

    const sum = ctx.createGain();
    sum.gain.value = 1 / Math.max(1, srcChannels);

    for (let ch = 0; ch < srcChannels; ch++) {
      const g = ctx.createGain();
      g.gain.value = 1;
      splitter.connect(g, ch, 0);
      g.connect(sum);
    }

    sum.connect(merger, 0, 0);
    src.connect(splitter);
    return merger;
  }

  // N -> 2 (take first two)
  if (dstChannels === 2 && srcChannels >= 2) {
    const splitter = ctx.createChannelSplitter(srcChannels);
    const merger = ctx.createChannelMerger(2);
    src.connect(splitter);
    splitter.connect(merger, 0, 0);
    splitter.connect(merger, 1, 1);
    return merger;
  }

  // Fallback: return as-is
  return src;
}

async function resampleAndCoerce(
  input: AudioBuffer,
  targetSampleRate: number,
  targetChannels: 1 | 2,
): Promise<AudioBuffer> {
  const srcChannels = input.numberOfChannels;
  const dstChannels = targetChannels;

  const frames = Math.max(1, Math.ceil(input.duration * targetSampleRate));
  const octx = new OfflineAudioContext(dstChannels, frames, targetSampleRate);

  const src = octx.createBufferSource();
  src.buffer = input;

  const node = buildChannelCoercer(octx, src, srcChannels, dstChannels);
  node.connect(octx.destination);
  src.start(0);

  const rendered = await octx.startRendering();
  return rendered;
}

function safeFileStem(name: string): string {
  const s = String(name || "podcast").trim() || "podcast";
  return s.replace(/[^\w\-]+/g, "_").slice(0, 80) || "podcast";
}

export function installExportAudioResolver(options: InstallOptions = {}): () => void {
  const fetchImpl = options.fetchImpl ?? fetch.bind(window);
  const timeoutMs = options.timeoutMs ?? 20_000;

  const g = window as any;
  const root: OmniCineExportGlobal = (g.__oc_export = g.__oc_export || {});

  // Preserve previous hooks so we can uninstall cleanly.
  const prevResolveAudio = root.resolveAudio;
  const prevResolvePodcastAudio = root.resolvePodcastAudio;
  const prevResolvePodcastMedia = root.resolvePodcastMedia;
  const prevRenderPodcastWav = root.renderPodcastWav;
  const prevInstalled = root._audioResolverInstalled;

  const resolvePodcastMedia = async (args: ResolveArgs): Promise<ResolvedMediaV1 | null> => {
    const clip = args.clip;

    const ref = normalizeClipMediaRef(clip as any, "audio");
    if (!ref) return null;

    const resolved = (await resolveMedia(ref)) as ResolvedMediaV1;

    // Optional hints (does not affect correctness; used by mixing decisions elsewhere)
    try {
      (resolved as any).__podcastHints = getPodcastRenderHintsFromResolved(resolved);
    } catch {
      // ignore
    }

    return resolved;
  };

  const resolvePodcastAudio = async (args: ResolveArgs): Promise<AudioBuffer> => {
    const exp = args.export;

    const targetSampleRate = Number(exp?.target?.sampleRate) || 48_000;
    const targetChannels = (Number(exp?.target?.channels) === 1 ? 1 : 2) as 1 | 2;

    const resolved = await resolvePodcastMedia(args);
    if (!resolved?.url) {
      throw new Error(
        `resolvePodcastAudio: unable to resolve media URL (clipId=${(args.clip as any)?.id ?? "unknown"})`,
      );
    }

    const ab = await fetchArrayBuffer(fetchImpl, resolved.url, timeoutMs);
    const decoded = await decodeAudio(ab);

    // Enforce export determinism: resample + channel coerce to export target.
    const coerced = await resampleAndCoerce(decoded, targetSampleRate, targetChannels);
    return coerced;
  };

  const resolveAudio: ResolveAudioFn = async ({ clip, track, export: exp }) => {
    return resolvePodcastAudio({ clip, track, export: exp });
  };

  // Install hooks
  root.resolvePodcastMedia = resolvePodcastMedia;
  root.resolvePodcastAudio = resolvePodcastAudio;
  root.resolveAudio = resolveAudio;

  // Escape hatch used by ExportBridge — DO NOT create object URLs here anymore.
  // ExportBridge will pass the blob through, and PodcastExportPanel will create the URL.
  root.renderPodcastWav = async (exp: PodcastExportV1, opts?: Partial<RenderPodcastWavOptions>) => {
    const blob = await renderPodcastExportToWavBlob(exp, {
      ...(opts || {}),
      resolveAudio: opts?.resolveAudio ?? root.resolveAudio ?? resolveAudio,
    });

    const stem = safeFileStem((exp as any)?.title || (exp as any)?.name || "podcast");
    const filename = `${stem}.wav`;

    return { blob, filename };
  };

  root._audioResolverInstalled = true;

  return () => {
    const g2 = window as any;
    const r2: OmniCineExportGlobal | undefined = g2.__oc_export;
    if (!r2) return;

    r2.resolveAudio = prevResolveAudio;
    r2.resolvePodcastAudio = prevResolvePodcastAudio;
    r2.resolvePodcastMedia = prevResolvePodcastMedia;
    r2.renderPodcastWav = prevRenderPodcastWav;
    r2._audioResolverInstalled = prevInstalled;

    // Don’t delete __oc_export; other bridges may share it.
  };
}
